import { action } from '@ember/object';
import Component from '@glimmer/component';

import { BlockRenderStep } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';

import type BlockRenderer from 'webapp/models-custom/block-renderer';
import type BlockSetup from 'webapp/models-custom/block-setup';
import type BlockState from 'webapp/models-custom/block-state';

interface Args {
	blockState: BlockState;
	blockRenderer: BlockRenderer;
	canEdit: boolean;
}

export default class BlockContentTitleComponent extends Component<Args> {
	get block() {
		return this.args.blockState.block;
	}

	get blockSetup(): BlockSetup {
		return this.block.blockSetup;
	}

	get visualization() {
		return this.blockSetup.visualization;
	}

	get hasTitle() {
		return this.block.name;
	}

	get titleChangeTrackingObj() {
		return {
			event: TrackingEvent.DESIGNER_BLOCK_NAME,
			data: { origin: this.block.trackingOrigin },
		};
	}

	@action
	setupView(_node: HTMLElement): void {
		const blockRenderer = this.args.blockRenderer;
		blockRenderer.registerStepHandler(BlockRenderStep.TITLE, this, (changes) => {
			const titleChanges = blockRenderer.updateTitleHeight();
			if (titleChanges) {
				changes.titleHeight = titleChanges;
			}
			return Promise.resolve();
		});
		blockRenderer.updateTitleHeight();

		// prepare functions for teardown, since they dont have access to this.args
		this.teardownFns.push(() => {
			blockRenderer.unregisterStepHandler(BlockRenderStep.TITLE, this);
		});
	}

	private teardownFns: (() => void)[] = [];

	@action
	teardownView(): void {
		this.teardownFns.forEach((fn) => fn());
	}
	<template>
		<div
			class="bc__title {{unless this.hasTitle 'bc__title--empty'}}"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			{{#if this.hasTitle}}
				<EditableModelPropertyComponent
					@model={{this.block}}
					@canEdit={{@canEdit}}
					@editableProperty="name"
					@trackingObj={{this.titleChangeTrackingObj}}
					@allowEmpty={{true}}
					@iconSize="dynamic"
				/>
			{{/if}}
		</div>
	</template>
}
